import { useAppDispatch, useAppState } from '../../context/appState';
import { MODAL_KEY } from '../../constants';
import { NavigationMenuModal } from '@/pages/container/Menu/NavigationMenu';
import { useCallback, useEffect, useState } from 'react';
import { EditAddressModal } from './EditAddress/EditAddress';
import { CartDrawer } from './Cart/Cart';
import { AnimatePresence } from 'framer-motion';
import { DatePickerModal } from './DatePicker/DatePicker';
import { LocationPopoverModal } from './LocationPopover/LocationPopover';
import { ConfirmModal } from './ConfirmModal/ConfirmModal';

export type WithModalClose = <T, A extends any[]>(
  callback: (...args: A) => T | Promise<T>,
) => (...args: A) => Promise<T>;

export type ModalVariant =
  | 'date-picker'
  | 'drawer-right'
  | 'drawer-left'
  | 'standard'
  | 'large'
  | 'form'
  | 'dialog';

interface ActiveModalProps {
  key: string;
  id: string;
  requestModalClose: () => void;
  withModalClose: WithModalClose;
}
export interface ModalProps<T> extends ActiveModalProps {
  children: ModalBody<T>;
  title?: string;
  variant?: ModalVariant;
  ariaLabel?: string;
  className?: string;
  darkenOverlay?: boolean;
}
export type ModalImpl = React.FC<ActiveModalProps>;

interface ModalBodyProps<T> {
  modalData: T;
  withModalClose: WithModalClose;
  requestModalClose: () => void;
}

export type ModalBody<T> = React.FC<ModalBodyProps<T>>;

export type ModalType<T> = React.FC<ModalProps<T>>;
/**
 * @todo - MainMenuDrawer, CartDrawer
 */
// import { CartDrawer } from './cart/CartDrawer';
// import { MainMenu } from './menu/MainMenu';

// 1. Static Registry: Avoids re-creating the object on every render
const MODAL_COMPONENTS: Record<string, ModalImpl> = {
  [MODAL_KEY.L_DRAWER]: NavigationMenuModal,
  [MODAL_KEY.EDIT_ADDRESS]: EditAddressModal,
  [MODAL_KEY.R_DRAWER]: CartDrawer,
  [MODAL_KEY.DATE_PICKER]: DatePickerModal,
  [MODAL_KEY.LOCATION_POPOVER]: LocationPopoverModal,
  [MODAL_KEY.CONFIRM_USE_DEFAULT_ADDRESS]: ConfirmModal,
  [MODAL_KEY.LEAVE_UNFINISHED_BOX]: ConfirmModal,

  // [MODAL_KEY.MAIN_MENU]: MainMenu,
};

export default () => {
  const { modal } = useAppState();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (modal) {
      document.body.classList.add('modal-active');
    } else {
      // If you aren't using onExitComplete, remove it here
      document.body.classList.remove('modal-active');
    }
  }, [modal]);

  // 2. Simplified withModalClose (No more Promise/setTimeout)
  const withModalClose: WithModalClose = (callback) => {
    return async (...args) => {
      const possibleEvent = args[0] as any;
      if (possibleEvent?.preventDefault) possibleEvent.preventDefault();

      dispatch({ type: 'CLOSE_MODAL' });
      return await callback(...args);
    };
  };

  const requestModalClose = useCallback(() => {
    dispatch({ type: 'REQUEST_MODAL_CLOSE' });
  }, [dispatch]);

  const ActiveModal = modal ? MODAL_COMPONENTS[modal] : null;

  return (
    <div className="modal-root">
      {/* 3. Let AnimatePresence handle the unmount delay */}
      <AnimatePresence mode="wait">
        {ActiveModal && (
          <ActiveModal
            key={modal!} // Key is crucial for AnimatePresence
            id={`${modal}--modal`}
            withModalClose={withModalClose}
            requestModalClose={requestModalClose}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
